import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AgGridReact } from 'ag-grid-react';
import {
  Box,
  Button,
  Container,
  Paper,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  InputAdornment,
  MenuItem,
  FormControl,
  InputLabel,
  Select
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Visibility,
  Refresh,
  Search,
  Clear,
  CheckCircle,
  Cancel as CancelIcon,
  Person,
  MoneyOff
} from '@mui/icons-material';
import api from '../../services/api';

// Helper function to format currency
const formatCurrency = (value) => {
  if (value === null || value === undefined) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
};

// Helper function to format number with commas
const formatNumber = (value) => {
  if (value === null || value === undefined) return '0';
  return new Intl.NumberFormat('en-US').format(value);
};

const CommissionRequestList = () => {
  const navigate = useNavigate();
  const gridRef = useRef();
  const [loading, setLoading] = useState(true);
  const [rowData, setRowData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Column Definitions
  const columnDefs = [
    {
      field: 'receiptNo',
      headerName: 'Receipt No',
      width: 160,
      cellRenderer: (params) => (
        <Chip
          label={params.value || 'N/A'}
          size="small"
          color="info"
          variant="outlined"
          sx={{ fontFamily: 'monospace' }}
        />
      )
    },
    {
      field: 'transDate',
      headerName: 'Date',
      width: 110,
      valueFormatter: (params) => {
        if (!params.value) return '';
        return new Date(params.value).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      }
    },
    {
      field: 'agentName',
      headerName: 'Agent',
      width: 180,
      cellRenderer: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Person fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="body2">{params.value || 'N/A'}</Typography>
        </Box>
      )
    },
    {
      field: 'creditAmount',
      headerName: 'Credit Amount',
      width: 150,
      cellRenderer: (params) => (
        <Typography variant="body2" color="primary" fontWeight="bold" sx={{ fontFamily: 'monospace' }}>
          {formatCurrency(params.value)}
        </Typography>
      )
    },
    {
      field: 'totalDeductions',
      headerName: 'Total Deductions',
      width: 150,
      cellRenderer: (params) => (
        <Typography variant="body2" color="error" fontWeight="bold" sx={{ fontFamily: 'monospace' }}>
          {formatCurrency(params.value)}
        </Typography>
      )
    },
    {
      field: 'netAmount',
      headerName: 'Net Amount',
      width: 150,
      cellRenderer: (params) => (
        <Typography variant="body2" color="success.main" fontWeight="bold" sx={{ fontFamily: 'monospace' }}>
          {formatCurrency(params.value)}
        </Typography>
      )
    },
    {
      field: 'applicantCount',
      headerName: 'Applicants',
      width: 120,
      cellRenderer: (params) => (
        <Chip
          label={formatNumber(params.value)}
          size="small"
          color="primary"
          icon={<Person />}
        />
      )
    },
    {
      field: 'deductionCount',
      headerName: 'Deductions',
      width: 120,
      cellRenderer: (params) => (
        <Chip
          label={formatNumber(params.value)}
          size="small"
          color="secondary"
          variant="outlined"
          icon={params.value > 0 ? <MoneyOff /> : null}
        />
      )
    },
    {
      field: 'isPaid',
      headerName: 'Status',
      width: 130,
      cellRenderer: (params) => (
        <Chip
          label={params.value ? 'PAID' : 'UNPAID'}
          color={params.value ? 'success' : 'warning'}
          size="small"
          icon={params.value ? <CheckCircle /> : <CancelIcon />}
        />
      )
    },
    {
      field: 'createdDate',
      headerName: 'Created',
      width: 120,
      valueFormatter: (params) => {
        if (!params.value) return '';
        return new Date(params.value).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      }
    },
    {
      headerName: 'Actions',
      width: 130,
      pinned: 'right',
      cellRenderer: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              color="primary"
              onClick={() => handleEdit(params.data)}
              disabled={params.data.isPaid}
            >
              <Edit />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              color="error"
              onClick={() => handleDeleteClick(params.data)}
              disabled={params.data.isPaid}
            >
              <Delete />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  // Default column definitions
  const defaultColDef = {
    sortable: true,
    filter: true,
    resizable: true,
    flex: 1,
    minWidth: 100
  };

  // Fetch data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/commissionrequest');
      console.log('Commission Requests Raw Data:', response.data);

      // Process data for display
      const processedData = response.data.map((item, index) => {
        // Get deductions and applicants
        const deductions = item.deductions || item.Deductions || [];
        const applicants = item.applicants || item.Applicants || [];
        
        // Calculate totals
        const totalDeductions = deductions.reduce((sum, d) => sum + (d.debitAmount || d.DebitAmount || 0), 0);
        const netAmount = (item.creditAmount || item.CreditAmount || 0) - totalDeductions;
        
        // Get agent name - now from the API response
        const agentName = item.agentName || item.AgentName || 'N/A';
        
        return {
          cid: item.cid || item.CID,
          receiptNo: item.receiptNo || item.ReceiptNo || 'N/A',
          transDate: item.transDate || item.TransDate,
          creditAmount: item.creditAmount || item.CreditAmount || 0,
          isPaid: item.isPaid || item.IsPaid || false,
          createdDate: item.createdDate || item.CreatedDate,
          remark: item.remark || item.Remark,
          deductions: deductions,
          applicants: applicants,
          totalDeductions: totalDeductions,
          netAmount: netAmount,
          applicantCount: applicants.length,
          deductionCount: deductions.length,
          agentName: agentName,
          _raw: item
        };
      });

      console.log('Processed Data:', processedData);
      setRowData(processedData);
      setFilteredData(processedData);
    } catch (error) {
      console.error('Error fetching commission requests:', error);
      let errorMessage = 'Failed to load commission requests.';
      
      if (error.response) {
        const responseData = error.response.data;
        if (typeof responseData === 'string') {
          errorMessage = responseData;
        } else if (responseData?.message) {
          errorMessage = responseData.message;
        }
      } else if (error.request) {
        errorMessage = 'No response from server. Please check your connection.';
      } else {
        errorMessage = error.message || 'An error occurred';
      }
      
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Filter data
  useEffect(() => {
    let filtered = [...rowData];

    if (searchText) {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter(item =>
        (item.receiptNo && item.receiptNo.toLowerCase().includes(searchLower)) ||
        (item.agentName && item.agentName.toLowerCase().includes(searchLower)) ||
        (item.remark && item.remark.toLowerCase().includes(searchLower))
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(item =>
        statusFilter === 'paid' ? item.isPaid : !item.isPaid
      );
    }

    setFilteredData(filtered);
  }, [searchText, statusFilter, rowData]);

  // Handlers
  const handleView = (data) => {
    navigate(`/commissions/view/${data.cid}`);
  };

  const handleEdit = (data) => {
    navigate(`/commissions/edit/${data.cid}`);
  };

  const handleDeleteClick = (data) => {
    setSelectedRequest(data);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedRequest) return;
    
    try {
      setDeleting(true);
      await api.delete(`/commissionrequest/${selectedRequest.cid}`);
      
      setSnackbar({
        open: true,
        message: 'Commission request deleted successfully!',
        severity: 'success'
      });
      
      setDeleteDialogOpen(false);
      setSelectedRequest(null);
      fetchData();
    } catch (error) {
      console.error('Error deleting commission request:', error);
      let errorMessage = 'Failed to delete commission request.';
      
      if (error.response) {
        const responseData = error.response.data;
        if (typeof responseData === 'string') {
          errorMessage = responseData;
        } else if (responseData?.message) {
          errorMessage = responseData.message;
        }
      } else if (error.request) {
        errorMessage = 'No response from server. Please check your connection.';
      } else {
        errorMessage = error.message || 'An error occurred';
      }
      
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setDeleting(false);
    }
  };

  const handleRefresh = () => {
    fetchData();
  };

  const handleClearFilters = () => {
    setSearchText('');
    setStatusFilter('all');
  };

  const handleExport = () => {
    if (gridRef.current) {
      gridRef.current.api.exportDataAsCsv();
    }
  };

  if (loading) {
    return (
      <Box sx={{ width: '100%', height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', height: '100vh', p: 0, bgcolor: '#f5f5f5' }}>
      <Paper sx={{ p: 1, height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Box>
            <Typography variant="h5" component="h1">
              Commission Requests
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Manage all commission requests
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<Add />}
              onClick={() => navigate('/commissions/create')}
            >
              New Request
            </Button>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={handleRefresh}
            >
              Refresh
            </Button>
          </Box>
        </Box>

        {/* Filters */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
          <TextField
            size="small"
            placeholder="Search by Receipt No, Agent, or Remark..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            sx={{ flexGrow: 1, minWidth: 200 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
              endAdornment: searchText && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchText('')}>
                    <Clear />
                  </IconButton>
                </InputAdornment>
              )
            }}
          />

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              label="Status"
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="paid">Paid</MenuItem>
              <MenuItem value="unpaid">Unpaid</MenuItem>
            </Select>
          </FormControl>

          <Button
            variant="outlined"
            size="small"
            startIcon={<Clear />}
            onClick={handleClearFilters}
          >
            Clear Filters
          </Button>

          <Box sx={{ flexGrow: 1 }} />

          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Typography variant="body2" color="textSecondary">
              Total: {filteredData.length} requests
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={handleExport}
            >
              Export CSV
            </Button>
          </Box>
        </Box>

        {/* AG Grid - Takes remaining space */}
        <Box sx={{ flex: 1, minHeight: 400, width: '100%' }}>
          <div className="ag-theme-alpine" style={{ height: '100%', width: '100%' }}>
            <AgGridReact
              ref={gridRef}
              rowData={filteredData}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              enableCellTextSelection={true}
              ensureDomOrder={true}
              pagination={true}
              paginationPageSize={50}
              paginationPageSizeSelector={[20, 50, 100]}
              overlayLoadingTemplate={
                '<span class="ag-overlay-loading-center">Loading commission requests...</span>'
              }
              overlayNoRowsTemplate={
                '<span class="ag-overlay-no-rows-center">No commission requests found</span>'
              }
              onGridReady={(params) => {
                if (params.api) {
                  setTimeout(() => {
                    params.api.sizeColumnsToFit();
                  }, 100);
                }
              }}
            />
          </div>
        </Box>

        {/* Summary Stats */}
        <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider', display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="caption" color="textSecondary">
              Total Requests
            </Typography>
            <Typography variant="h6">{filteredData.length}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="textSecondary">
              Total Credit Amount
            </Typography>
            <Typography variant="h6" color="primary" sx={{ fontFamily: 'monospace' }}>
              {formatCurrency(filteredData.reduce((sum, item) => sum + (item.creditAmount || 0), 0))}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="textSecondary">
              Total Deductions
            </Typography>
            <Typography variant="h6" color="error" sx={{ fontFamily: 'monospace' }}>
              {formatCurrency(filteredData.reduce((sum, item) => sum + (item.totalDeductions || 0), 0))}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="textSecondary">
              Total Net Amount
            </Typography>
            <Typography variant="h6" color="success" sx={{ fontFamily: 'monospace' }}>
              {formatCurrency(filteredData.reduce((sum, item) => sum + (item.netAmount || 0), 0))}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="textSecondary">
              Paid / Unpaid
            </Typography>
            <Typography variant="h6">
              {filteredData.filter(item => item.isPaid).length} / {filteredData.filter(item => !item.isPaid).length}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="textSecondary">
              Total Applicants
            </Typography>
            <Typography variant="h6">
              {formatNumber(filteredData.reduce((sum, item) => sum + (item.applicantCount || 0), 0))}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="textSecondary">
              Total Deduction Entries
            </Typography>
            <Typography variant="h6">
              {formatNumber(filteredData.reduce((sum, item) => sum + (item.deductionCount || 0), 0))}
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this commission request?
            <br /><br />
            <strong>Receipt No:</strong> {selectedRequest?.receiptNo || 'N/A'}<br />
            <strong>Amount:</strong> {formatCurrency(selectedRequest?.creditAmount)}<br />
            <strong>Status:</strong> {selectedRequest?.isPaid ? 'PAID' : 'UNPAID'}<br />
            <strong>Applicants:</strong> {formatNumber(selectedRequest?.applicantCount)}<br />
            <strong>Deductions:</strong> {formatNumber(selectedRequest?.deductionCount)}
            <br /><br />
            {!selectedRequest?.isPaid && (
              <Alert severity="warning">
                This action cannot be undone. Make sure this request is not needed.
              </Alert>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={20} /> : <Delete />}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CommissionRequestList;